/**
 * Gestione dei giorni "a prescindere dal fuso orario".
 *
 * In tutto il calendario un giorno è sempre una stringa "YYYY-MM-DD" (nessuna ora,
 * nessun offset). Per fare aritmetica sui giorni (addDays, weekday, confronti...) serve
 * comunque un oggetto Date, e lì il fuso del browser può introdurre uno slittamento di
 * un giorno se non si sta attenti. Regola seguita da questo file:
 *
 *  1. Una stringa "YYYY-MM-DD" viene sempre ancorata a mezzanotte UTC con utcDate()
 *     (mai `new Date(ymd)` diretto: se la stringa contiene un'ora, o non è in formato
 *     ISO puro, il browser la interpreta come ora locale).
 *  2. Da quel Date si leggono SEMPRE i componenti con i getter UTC (getUTCDate,
 *     getUTCDay, getUTCMonth, ...), mai i getter locali: il Date è un "segnaposto"
 *     fissato a UTC di proposito, i getter locali lo farebbero slittare in base al
 *     fuso del browser.
 *  3. Eccezione speculare: quando serve il giorno "di oggi" (calendario.today()), non
 *     si parte da un'ora già nota ma dall'orologio del dispositivo — lì bisogna usare i
 *     getter LOCALI (getFullYear/getMonth/getDate), mai `new Date().toISOString()`.
 *     toISOString() converte in UTC: per un utente ad ovest di Greenwich (es. Americhe)
 *     ci sono diverse ore ogni sera in cui in UTC è già "domani" mentre localmente è
 *     ancora "oggi" — usare l'ora UTC farebbe vedere il giorno slittato in avanti di 1.
 */
const calendario = {
    /** Il giorno "di oggi" per l'utente corrente, come "YYYY-MM-DD" — usa l'orologio
     * locale del dispositivo, mai UTC (vedi nota in cima al file). */
    today: function () {
        const date = new Date();
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    },

    countDays: function (from, to) {
        from = from ? new Date(from) : new Date();
        to = to ? new Date(to) : new Date();
        return Math.ceil((to-from)/86400000);
    },
    fitToUnix: function (fitTimestamp) {
        const FIT_TO_UNIX_OFFSET = 631065600;
        return new Date((fitTimestamp + FIT_TO_UNIX_OFFSET) * 1000);
    },

    fitToTime: function (fitTimestamp) {
        const FIT_TO_UNIX_OFFSET = 631065600;
        const date = new Date((fitTimestamp + FIT_TO_UNIX_OFFSET) * 1000);
        return date.toISOString().substring(11, 19); // "HH:mm:ss"
    },
    makeDays: function (from, to) {
        let current = ""+from;
        let dateCurrent = calendario.utcDate(current);
        let dateLast = calendario.utcDate(to);
        let days = [];
        let safe = 0;
        let maxSafe = 6000
        while ((safe < maxSafe) && (dateCurrent <= dateLast)) {
            safe += 1;
            if (safe===maxSafe) {
                alert("Hai raggiunto il limite massimo di " +maxSafe+" giorni.");
            }
            let dayString = calendario.date2ymd(dateCurrent);
            let day = {
                date: dayString,
            }
            if (!days.find(el=>el.date===dayString)) {
                days.push(day);
            }
            dateCurrent = calendario.utcDate(calendario.addDays(dayString, 1));
        }
        return days;
    },

      prevWeek: function (ymd) {
        let firstMonday = ymd;//calendario.getMonday(ymd);
        let from = calendario.addDays(firstMonday, -7);
        let to = calendario.addDays(firstMonday,0);
        return {
          from: from,
          to: to
        }
      },

      nextWeek: function (ymd) {
        let lastMonday = ymd;//calendario.getMonday(ymd);
        let from = calendario.addDays(lastMonday,0);
        let to = calendario.addDays(lastMonday, +7);
        return {
          from: from,
          to: to
        }
      },

      addDays: function(ymd, days) {
        let date = calendario.utcDate(ymd);
        date.setUTCDate(date.getUTCDate() + days)
        return calendario.date2ymd(date);
      },

    diffInDays: function(ymd1, ymd2) {
        const date1 = calendario.utcDate(ymd1);
        const date2 = calendario.utcDate(ymd2);
        const msPerDay = 24 * 60 * 60 * 1000;
        const utc1 = calendario.utcDate(date1.getUTCFullYear(), date1.getUTCMonth(), date1.getUTCDate());
        const utc2 = calendario.utcDate(date2.getUTCFullYear(), date2.getUTCMonth(), date2.getUTCDate());
        return Math.round((utc2 - utc1) / msPerDay);
    },


    formatDateShort: function(date) {
        const opzioni = { day: 'numeric', month: 'short', year: 'numeric' };
        return new Intl.DateTimeFormat('it-IT', opzioni).format(date);
    },

    ymd2dateShort: function(ymd) {
        const date = calendario.utcDate(ymd);
        const opzioni = { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' };
        return new Intl.DateTimeFormat('it-IT', opzioni).format(date);
    },

    /** Es. "lun 17 lug 26". Nessun parametro di fuso orario: ymd è già un giorno "puro"
     * (vedi nota in cima al file), quindi va formattato forzando timeZone:'UTC' — senza,
     * per chi si trova ad ovest di Greenwich (es. Canada) l'istante UTC di mezzanotte
     * slitta al giorno prima, sfasando di un giorno etichetta e posizione in griglia
     * rispetto agli allenamenti effettivamente assegnati a quel ymd. */
    formatCalendarDay: function(ymd) {
        const date = calendario.utcDate(ymd);
        const opzioni = { weekday: 'short', day: '2-digit', month: 'short', year: '2-digit', timeZone: 'UTC' };
        return new Intl.DateTimeFormat('it-IT', opzioni).format(date);
    },

    ymd2dmy: function(ymd) {
        const date = calendario.utcDate(ymd);
        const d = String(date.getUTCDate()).padStart(2, '0');
        const m = String(date.getUTCMonth() + 1).padStart(2, '0');
        const y = date.getUTCFullYear();
        return `${d}/${m}/${y}`;
    },



    getMonthName: function (ymd) {
        const date = calendario.utcDate(ymd);
        return date.toLocaleString("default",{month:"long", timeZone: 'UTC'})
    },

    getMonthYear: function (ymd) {
        const date = calendario.utcDate(ymd);
        return date.toLocaleString("default",{month:"long", year: 'numeric', timeZone: 'UTC'})
    },

    getWeek: function (ymd) {
        const date = calendario.utcDate(ymd);
        var onejan = new Date(date.getFullYear(), 0, 1);
        return Math.ceil((((this - onejan) / 86400000) + onejan.getDay() + 1) / 7);
    },

    getFirstDayOfThisMonth: function(ymd) {
        return calendario.getFirstDayOfNthMonth(ymd, 0);
    },

    getFirstDayOfNextMonth: function(ymd) {
        return calendario.getFirstDayOfNthMonth(ymd, 1);
    },

    getFirstDayOfPrevMonth: function(ymd) {
        return calendario.getFirstDayOfNthMonth(ymd, -1);
    },

    dateFirstDayOfNthMonth: function(ymd, inc) {
        const date = calendario.utcDate(ymd);
        const out = calendario.utcDate(date.getUTCFullYear(), date.getUTCMonth() + inc, 1);
        return out;
    },

    getFirstDayOfNthMonth: function(ymd, inc) {
        const firstDay = calendario.dateFirstDayOfNthMonth(ymd,inc);
        return calendario.date2ymd(firstDay);
    },

    timezone: function () {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    },
    localDate: function (date) {
        let minutes = date.getTimezoneOffset();
        let localTimestamp = date.valueOf() - minutes*60*1000;
        return new Date(localTimestamp);
    },

    /** Converte una stringa naive "Y-m-d H:i:s" (UTC, senza timezone, es.
     * chat_ultimo.utc_data_inviato/messaggio.utc_data_inviato dal backend) in un Date
     * corretto: il browser interpreta una stringa così come ora locale, non UTC, quindi
     * senza questa correzione l'orario mostrato resta indietro dell'offset del fuso
     * (es. 10:15 invece di 12:15 in CEST/UTC+2). */
    utcStringToLocalDate: function (utcString) {
        if (!utcString) return null;
        return calendario.localDate(new Date(utcString));
    },

    date2ymd: function (date) {
        if (typeof date === "string") return date;
        if (!date) {
            date = calendario.utcDate();
        }
        return date.toISOString().substr(0,10);
    },

    /** Es. "2026-07-17 09:05:03" (ora locale, non UTC) */
    date2ymdhms: function (date) {
        if (typeof date === "string") return date;
        date = date || new Date();
        const pad = (n) => String(n).padStart(2, '0');
        const ymd = `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}`;
        const hms = `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
        return `${ymd} ${hms}`;
    },

    /** alias date2ymd */
    formatDateYmd: function (date) {
        return calendario.date2ymd(date);
    },

    utcDate: function (ymd) {
        if (!ymd) {
            // Non "new Date().toISOString()": darebbe la data UTC, non quella locale
            // dell'utente (vedi nota timezone in cima al file). calendario.today() legge
            // l'orologio locale del dispositivo.
            ymd = calendario.today();
        } else if (arguments.length===3) {
            ymd = new Date(Date.UTC(arguments[0], arguments[1], arguments[2])).toISOString();
        } else if (typeof ymd === 'number') {
            ymd = new Date(ymd).toISOString();
        } else if (typeof ymd !== 'string') {
            ymd = ymd.toISOString();
        }
        let dateString = ymd.substr(0,10);
        let utc = dateString+"T00:00:00.000Z";
        return new Date(utc);
    },

    /** Nome del giorno della settimana di ymd. Nessun parametro di fuso orario: ymd è
     * già un giorno "puro" (vedi nota in cima al file), quindi va letto forzando
     * timeZone:'UTC' sul Date ancorato da utcDate() — passare il fuso del browser (o
     * qualunque altro) farebbe slittare l'istante UTC di mezzanotte al giorno prima per
     * chi si trova ad ovest di Greenwich. */
    getWeekDay: function (ymd) {
        const date = calendario.utcDate(ymd);
        var weekDay = date.toLocaleString("en-US", {
            timeZone: 'UTC',
            weekday: 'long'
        });
        return weekDay;
    },

    getMonday: function (ymd = "") {
        let date = calendario.dateMonday(ymd);
        if (!date) return false;
        return calendario.date2ymd(date);
    },

    getSunday: function (ymd = "") {
        let date = calendario.dateSunday(ymd);
        if (!date) return false;
        return calendario.date2ymd(date);
    },

    getDay: function (ymd) { 
        let ref = calendario.utcDate(ymd);
        return ref.getUTCDay();
    },
    getDate: function (ymd) { 
        let ref = calendario.utcDate(ymd);
        return ref.getUTCDate();
    },

    dateMonday: function (ymd) {
        let ref = calendario.utcDate(ymd);
        let incDay = 0;
        let weekDay = calendario.getDay(ymd);
        if (weekDay === 0) {
            incDay = 6;
        } else {
            incDay = weekDay - 1;
        }
        let first = calendario.getDate(ymd) - incDay; // First day is the day of the month - the day of the week
        ref.setUTCDate(first);
        return ref;
    },

    dateSunday: function (ymd) {
        let ref = calendario.utcDate(ymd);
        let incDay = 0;
        let weekDay = calendario.getDay(ymd);
        if (weekDay) {
            incDay = 7 - weekDay;
        }
        let first = calendario.getDate(ymd) + incDay; // First day is the day of the month - the day of the week
        ref.setUTCDate(first);
        return ref;
    },
    nextMonday: function (ymd) {
        return calendario.addDays(calendario.getMonday(ymd), 7);
    },

    inLimits: function (start, end, day) {
        const dateLimitStart = calendario.utcDate(start);
        const dateLimitEnd = calendario.utcDate(end);
        const date = calendario.utcDate(day);
        const inLimits =  (dateLimitStart.getTime() <= date.getTime()) && (dateLimitEnd.getTime() >= date.getTime());
        return inLimits;
    },
}
export default calendario;

